// ---------------------------------------------------------------------------
// Closed sets
// ---------------------------------------------------------------------------
//
// A closed set is either a REAL schema enum — those live in `enums.ts`, which
// re-exports them and is the only file allowed to name them — or a field the
// API types as `String` while the set behind it is a Postgres enum or an
// exported allowlist. This module is the only place the second kind is written
// down.
//
// Every list below is verified against the server source named in the comment
// (each one read from schema.gql). None of it is guessed from usage.
//
// Three casings exist for the same concept and this is a real trap:
//   "issue"  Favorite.type, GlobalSearchHit.kind
//   "Issue"  trash itemType   (capital I; anything else -> ValidationError)
//   "ISSUE"  MapClusterItem.type
// Use ISSUE_FAVORITE_TYPE / TRASH_ITEM_TYPE / MAP_CLUSTER_ISSUE_TYPE below
// rather than typing a literal.

/**
 * `SystemRole` omits `owner`, which IS a real seeded system role in
 * the `@salestrak/permissions` registry. Compare role names against
 * this, not against the enum, when "is this a built-in role" is the question.
 */
export const SEEDED_SYSTEM_ROLES = ["owner", "admin", "member", "limited_member", "guest"] as const;
export type SeededSystemRole = (typeof SEEDED_SYSTEM_ROLES)[number];

// --- String-typed closed sets ---------------------------------------------

function guard<T extends string>(values: readonly T[]) {
  const set = new Set<string>(values);
  return (value: string | null | undefined): value is T =>
    value != null && set.has(value);
}

/**
 * `EntityView.type` and `EntityView.builtInKey`. Allowlists in
 * `apps/api/core/src/entity-views/entity-view-types.ts`. Both are
 * ADDITIVE-ONLY server-side: a value that has been persisted keeps validating.
 */
export const ENTITY_VIEW_TYPES = ["list", "board", "dashboard"] as const;
export type EntityViewType = (typeof ENTITY_VIEW_TYPES)[number];
export const isEntityViewType = guard(ENTITY_VIEW_TYPES);

/**
 * `EntityView.scopeKind` (`ENTITY_VIEW_SCOPE_KINDS`). A `workspace` scope's
 * id IS the workspace id; only dashboards live there. A `team` scope's id is
 * the team id; it holds the team page's built-in record tabs (Projects).
 */
export const ENTITY_VIEW_SCOPE_KINDS = ["workflow", "workspace", "team"] as const;
export type EntityViewScopeKind = (typeof ENTITY_VIEW_SCOPE_KINDS)[number];
export const isEntityViewScopeKind = guard(ENTITY_VIEW_SCOPE_KINDS);

/** `Stage.type`. Postgres enum `stage_type` (`src/stages/stage.entity.ts`). */
export const STAGE_TYPES = ["pending", "inProgress", "done", "canceled"] as const;
export type StageType = (typeof STAGE_TYPES)[number];
export const isStageType = guard(STAGE_TYPES);

/**
 * The two terminal stage types — work that has stopped, whether it landed or
 * not. The server draws the same line (`CLOSED_STAGE_TYPES` in
 * `src/stages/effective-cycle.ts`), which is why this is typed against
 * `StageType`: adding a fifth stage type makes tsc ask which side it falls on
 * rather than letting it default to open.
 */
export const CLOSED_STAGE_TYPES = ["done", "canceled"] as const satisfies ReadonlyArray<StageType>;

export function isClosedStageType(type: string | null | undefined): boolean {
  return CLOSED_STAGE_TYPES.includes(type as (typeof CLOSED_STAGE_TYPES)[number]);
}

/** `IssueAge.status`. Postgres enum `issue_age_status`. Also `ActivityWhereInput.ageStatus`. */
export const ISSUE_AGE_STATUSES = ["onTrack", "atRisk", "offTrack"] as const;
export type IssueAgeStatus = (typeof ISSUE_AGE_STATUSES)[number];
export const isIssueAgeStatus = guard(ISSUE_AGE_STATUSES);

/**
 * `UpdateCycle.step` / `UpdateCycleInput.step` — the unit of an update cycle,
 * whether it is pinned on a Stage, defaulted on a Workflow, or set on one
 * Project. `UpdateStep` in `src/stages/effective-cycle.ts`.
 */
export const UPDATE_STEPS = ["hours", "days", "weeks", "months", "years"] as const;
export type UpdateStep = (typeof UPDATE_STEPS)[number];
export const isUpdateStep = guard(UPDATE_STEPS);

/**
 * `User.memberStatus`. Postgres enum `user_member_status`.
 * `Workspace.memberStatus` is the same set OR null (the viewer has no row).
 * `left` is the member who removed themself: the row stays so their work and
 * history keep naming them, and the only way back is a fresh invite.
 */
export const MEMBER_STATUSES = ["active", "invited", "expired", "suspended", "declined", "left"] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];
export const isMemberStatus = guard(MEMBER_STATUSES);

/** `Media.status`. Postgres enum `media_status`. */
export const MEDIA_STATUSES = ["pending", "uploaded"] as const;
export type MediaStatus = (typeof MEDIA_STATUSES)[number];
export const isMediaStatus = guard(MEDIA_STATUSES);

/** `CreateMediaUploadInput.kind`. `MEDIA_KIND_FOLDERS` in `media.service.ts`. */
export const MEDIA_KINDS = ["avatar", "media"] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];
export const isMediaKind = guard(MEDIA_KINDS);

/** `Webhook.method`. Postgres enum `http_method`. */
export const WEBHOOK_METHODS = ["get", "patch", "post", "put", "delete"] as const;
export type WebhookMethod = (typeof WEBHOOK_METHODS)[number];
export const isWebhookMethod = guard(WEBHOOK_METHODS);

/** `Webhook.resource`. Postgres enum `webhook_resource` — ONE value today. */
export const WEBHOOK_RESOURCES = ["issues"] as const;
export type WebhookResource = (typeof WEBHOOK_RESOURCES)[number];
export const isWebhookResource = guard(WEBHOOK_RESOURCES);

/** `Webhook.trigger`. Postgres enum `webhook_trigger`. */
export const WEBHOOK_TRIGGERS = [
  "issueStageChange",
  "issueCreatedOnStage",
  "issueDeleted",
  "issueTrashed",
] as const;
export type WebhookTrigger = (typeof WEBHOOK_TRIGGERS)[number];
export const isWebhookTrigger = guard(WEBHOOK_TRIGGERS);

/** `Widget.type`. `WIDGET_TYPES` in `src/widgets/widget-types.ts`. */
export const WIDGET_TYPES = [
  "note",
  "embed",
  // Retired on the client; the key stays so persisted rows still validate.
  "section-header",
  "metric",
  "dimension-bar",
  "dimension-battery",
  "type-mix",
  "series-line",
  "stage-age",
  "stage-moves",
  "projects-closed",
  "projects-by-stage",
  "most-active-users",
  "entity-list",
  "favorites-list",
  "activity-list",
  "team-members",
  "portfolio-map",
  "stage-throughput",
  "stat-strip",
  "outcome-performance",
  "workflow-roster",
  "stage-funnel",
  "stage-conversion",
  "assigned-list",
  "added-list",
  "recent-list",
  "agenda",
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];
export const isWidgetType = guard(WIDGET_TYPES);

// `Widget.view` is deliberately OPEN — dashboards derive values like
// `widget-view-workflow:<id>`. A new view is a new value, not a schema change.
// There is no set to guard.

/** `AutomationStep.type`. `src/automations/catalog/step-types.ts`. */
export const AUTOMATION_STEP_TYPES = [
  "updateIssueField",
  "moveIssueStage",
  "setUpdateCycle",
  "assignIssue",
  "addNote",
  "createIssue",
  "archiveIssue",
  "updateContactHealth",
  "notify",
  "sendEmail",
  "webhook",
  "branch",
  "filter",
] as const;
export type AutomationStepType = (typeof AUTOMATION_STEP_TYPES)[number];
export const isAutomationStepType = guard(AUTOMATION_STEP_TYPES);

/** `AutomationTrigger.sourceKey`. `src/automations/catalog/trigger-sources.ts`. */
export const AUTOMATION_TRIGGER_SOURCES = [
  "entity",
  "issue.created",
  "issue.stage_changed",
  "issue.workflow_changed",
  "issue.field_changed",
  "issue.assigned",
  "issue.age_status_changed",
  "issue.address_changed",
  "issue.archived",
  "note.added",
  "form.submitted",
  "schedule",
] as const;
export type AutomationTriggerSource = (typeof AUTOMATION_TRIGGER_SOURCES)[number];
export const isAutomationTriggerSource = guard(AUTOMATION_TRIGGER_SOURCES);

/**
 * `StructuredCondition.unit` — the operand of a relative `currentDate`
 * comparison ("at least N <unit> before"). `ConditionDateUnit` in
 * `src/automations/catalog/structured-condition.ts`.
 *
 * Same five values as `UPDATE_STEPS` and deliberately a separate set: the
 * update cycle and the automation catalog are independent server sets that
 * happen to coincide today. Aliasing one to the other means a unit added to
 * the catalog silently widens `UpdateStep` too.
 */
export const CONDITION_DATE_UNITS = ["hours", "days", "weeks", "months", "years"] as const;
export type ConditionDateUnit = (typeof CONDITION_DATE_UNITS)[number];
export const isConditionDateUnit = guard(CONDITION_DATE_UNITS);

/** `AutomationTrigger.entityType` / `AutomationRun.entityType`. */
export const AUTOMATION_ENTITY_TYPES = ["issue"] as const;
export type AutomationEntityType = (typeof AUTOMATION_ENTITY_TYPES)[number];
export const isAutomationEntityType = guard(AUTOMATION_ENTITY_TYPES);

/** `GlobalSearchHit.kind`. */
export const SEARCH_HIT_KINDS = ["issue", "user", "team"] as const;
export type SearchHitKind = (typeof SEARCH_HIT_KINDS)[number];
export const isSearchHitKind = guard(SEARCH_HIT_KINDS);

// `TeamMembership` role is the real enum `TeamMembershipRole`, re-exported
// above. `Team.viewerRole` returns it; the `role` arg of `addTeamMembers` is
// still typed `String`, and the enum type is assignable to it.

/** `EffectivePermissionEntry.verdict`. `RoleVerdict` in `authorization.types.ts`. */
export const PERMISSION_VERDICTS = ["yes", "no", "if_created", "if_shared"] as const;
export type PermissionVerdict = (typeof PERMISSION_VERDICTS)[number];
export const isPermissionVerdict = guard(PERMISSION_VERDICTS);

/** `PermissionKeyMeta.category`. The `Category` typedef in `registry.js`. */
export const PERMISSION_CATEGORIES = [
  "collaboration",
  "manage",
  "workflows",
  "create_delete",
  "custom_fields",
  "forms",
  "comments",
  "workspace",
] as const;
export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number];
export const isPermissionCategory = guard(PERMISSION_CATEGORIES);

/**
 * Every permission key. The `@salestrak/permissions` registry is the
 * source; this list mirrors it. Also the value set of
 * `PermissionDenied.requiredPermission`.
 */
export const PERMISSION_KEYS = [
  "manage.users",
  "manage.teams",
  "manage.edit_workflows",
  "widgets.manage",
  "create.roles",
  "create.workflows",
  "delete.workflows",
  "create.items",
  "delete.items",
  "archive.items",
  "custom_fields.manage",
  "trash.manage",
  "workspace.integrations",
  "issue_types.manage",
  "reasons.manage",
  "automations.manage",
  "issue_sources.manage",
  "forms.manage",
  "comments.edit_own",
  "comments.delete_own",
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];
export const isPermissionKey = guard(PERMISSION_KEYS);

/**
 * `Notification.type`, bucketized by `bucketizeAction()` from the free-form
 * `action` string. Null when `action` is null. `Notification.action` itself is
 * OPEN — human text like "mentioned you" — so it has no set.
 */
export const NOTIFICATION_TYPES = ["assignment", "update", "reminder", "mention"] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
export const isNotificationType = guard(NOTIFICATION_TYPES);

/** `InspectionMetrics.trend`. `computeTrend` in `dashboard.service.ts`. */
export const TREND_DIRECTIONS = ["up", "down", "flat"] as const;
export type TrendDirection = (typeof TREND_DIRECTIONS)[number];
export const isTrendDirection = guard(TREND_DIRECTIONS);

/** `Query.issueGroupCounts(groupBy:)` — the categorical tokens. */
export const ISSUE_GROUP_BY_KEYS = [
  "stageId",
  "issueTypeId",
  "workflowId",
  "age.status",
  "assignees.entityId",
  "team",
  "issueSourceId",
] as const;
export type IssueGroupByKey = (typeof ISSUE_GROUP_BY_KEYS)[number];
export const isIssueGroupByKey = guard(ISSUE_GROUP_BY_KEYS);
// The guard covers the categorical tokens only. `groupBy` ALSO accepts the
// time-series grammar below.

// --- The time-series groupBy grammar --------------------------------------
//
// `Query.issueGroupCounts(groupBy:)` accepts a dotted token that buckets by
// period instead of by category: `<measure>.<grain>`, or
// `<dimension>.<measure>.<grain>` to split each period. `parseTimeSeriesGroupBy`
// in `src/issues/issues.service.ts` parses it, and an unparseable token falls
// through to the categorical path and returns nothing — a typo is silence, not
// an error. Build tokens with `timeSeriesGroupBy` rather than by concatenating.

/** The timestamp a period buckets on. `TIME_SERIES_DATE_COLUMNS` keys. */
export const TIME_SERIES_MEASURES = ["created", "updated"] as const;
export type TimeSeriesMeasure = (typeof TIME_SERIES_MEASURES)[number];
export const isTimeSeriesMeasure = guard(TIME_SERIES_MEASURES);

/** Period width. `day` powers the heatmap; `week`/`month` the line charts. */
export const TIME_SERIES_GRAINS = ["day", "week", "month"] as const;
export type TimeSeriesGrain = (typeof TIME_SERIES_GRAINS)[number];
export const isTimeSeriesGrain = guard(TIME_SERIES_GRAINS);

/** Optional split — one series per value of this dimension. `TIME_SERIES_DIMENSIONS`
 *  in the service: the scalar columns, plus `assignee` (through `issue_assignment`,
 *  so a project with two assignees counts once per line), `team` (through the
 *  assignees' memberships, distinct per project) and `teams` (the project's own
 *  Assigned Team). */
export const TIME_SERIES_DIMENSIONS = ["stage", "workflow", "type", "source", "assignee", "team", "teams"] as const;
export type TimeSeriesDimension = (typeof TIME_SERIES_DIMENSIONS)[number];
export const isTimeSeriesDimension = guard(TIME_SERIES_DIMENSIONS);

/** Assemble a time-series `groupBy` token. Omit `dimension` for one series. */
export function timeSeriesGroupBy(
  measure: TimeSeriesMeasure,
  grain: TimeSeriesGrain,
  dimension?: TimeSeriesDimension,
): string {
  return dimension ? `${dimension}.${measure}.${grain}` : `${measure}.${grain}`;
}

/**
 * The outcome time series: `Query.activityGroupCounts(groupBy: "outcome.<grain>")`
 * keys every posted Update `<reasonId>|<bucket start>`, the bucket truncated in
 * the viewer's zone (the query's `timezone`). The same `|` separator as the
 * issue time series, so one splitter reads both.
 */
export function outcomeSeriesGroupBy(grain: TimeSeriesGrain): string {
  return `outcome.${grain}`;
}

/** `Query.activityGroupCounts(groupBy:)` — the categorical tokens. `GROUP_EXPRESSIONS`
 *  in the service; the service also accepts `outcomeSeriesGroupBy`'s series token. */
export const ACTIVITY_GROUP_BY_KEYS = [
  "actor",
  "actor.issue",
  "stage.entered",
  "outcome",
  "actor.outcome",
  "entityType",
  "entityType.unique",
  "update.issue",
] as const;
export type ActivityGroupByKey = (typeof ACTIVITY_GROUP_BY_KEYS)[number];
export const isActivityGroupByKey = guard(ACTIVITY_GROUP_BY_KEYS);

/** `ApiKeyPermissions` is booleans on the wire; this is the column behind it. */
export const API_KEY_PERMISSIONS = ["all", "create", "read", "update", "delete"] as const;
export type ApiKeyPermission = (typeof API_KEY_PERMISSIONS)[number];
export const isApiKeyPermission = guard(API_KEY_PERMISSIONS);

// --- The three casings of "issue" -----------------------------------------

/** `Favorite.type`. Lowercase. Only this one value exists. */
export const ISSUE_FAVORITE_TYPE = "issue";
/** `restoreTrashItem`/`deleteTrashItemPermanently` `itemType`. CAPITAL I. */
export const TRASH_ITEM_TYPE = "Issue";
/** `MapClusterItem.type`. ALL CAPS. */
export const MAP_CLUSTER_ISSUE_TYPE = "ISSUE";

// --- Sets the server does NOT publish -------------------------------------
//
// These are typed `String` and no exported allowlist exists on the server, so
// the client cannot verify them. They are listed here so nobody writes a guard
// that only looks authoritative:
//
//   AutomationStepType.category / AutomationTemplate.category
//   MapClusterWhereInput.entityTypes
//   DashboardDrilldownInput.subject / sortField
//   ActivityWhereInput.triggeredBy
//   UserPreferences.themeMode / fontSize / themeColor / defaultRoute
//     — the CLIENT registry owns these; the server persists opaque strings.
//   UserPreferences.toastPosition
//     — the library ToastProvider's six anchors. `components/app-toast-provider.tsx`
//       owns the list (TOAST_POSITION_OPTIONS); do not restate it here.
